import { DropdownIndicator } from '@ui/DropdownIndicator';
import { responsiveFontSizes } from '@mui/material';
import {
  PaletteColor,
  PaletteColorOptions,
  createTheme,
} from '@mui/material/styles';
import { Primitives } from './primitive.constants';
import { getTooltipBaseStyles } from './tooltip.styles';
import {
  ghostButtonLargeStyles,
  ghostButtonPrimaryStyles,
} from '@ui/ghost-button.styles';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    menuTitle: React.CSSProperties;
    subtitle3: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    menuTitle?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    menuTitle: true;
    subtitle3: true;
  }
}

const baseTheme = createTheme({
  zIndex: {
    readonlyBlocker: 9999,
  },
  typography: {
    fontSize: 12,
    fontFamily: 'Roboto',
    fontWeightRegular: 400,
    h1: {
      fontWeight: 500,
      fontSize: 30,
    },
    h2: {
      fontWeight: 500,
      fontSize: 26,
    },
    h3: {
      fontWeight: 500,
      fontSize: 22,
    },
    h4: {
      fontWeight: 500,
      fontSize: 18,
    },
    h5: {
      fontWeight: 500,
      fontSize: 14,
    },
    h6: {
      fontWeight: 500,
      fontSize: 12,
    },
    body1: {},
    body2: {},
    // TODO: Setup sample page with typography
    subtitle3: {
      fontWeight: 400,
      fontSize: 12,
      textTransform: 'uppercase',
      letterSpacing: '5%',
    },
    caption: {
      color: '#A7A7A7',
    },
    menuTitle: {
      color: '#A7A7A7',
      paddingLeft: 10,
    },
  },
  palette: {
    custom: {
      focus: '#0B99FF',
    },
    primary: {
      main: '#312884', // violet - wat? /henrik
      light: '#D5D9E8', // gray
      dark: '#bCC0CF', // dark-gray
    },
    secondary: {
      main: '#1BA1AA', // green
      light: '#F5F5F5', // dark-white
    },
    warning: {
      main: '#F6841A', // orange
    },
    background: {
      default: '#FDFBF8',
    },
    action: {
      selected: '#D5D9E8', // gray
    },
    text: {
      primary: Primitives.colors.transparent_gray[800],
      secondary: Primitives.colors.transparent_gray[400],
      disabled: Primitives.colors.transparent_gray[200],
      subtle: Primitives.colors.transparent_gray[200],
    },
    neutral: {
      dark: 'rgba(0, 0, 0, 0.8)',
      main: 'rgba(0, 0, 0, 0.6)',
      medium: 'rgba(0, 0, 0, 0.5)',
      light: 'rgba(0, 0, 0, 0.4)',
      lighter: 'rgba(0, 0, 0, 0.2)',
      lightest: 'rgba(0, 0, 0, 0.1)',
      superLight: 'rgba(0, 0, 0, 0.05)',
      contrastText: '#000',
    },
    styledBackground: {
      main: '#E6E8EF', // white-violet
    },
    warmSurface: {
      main: Primitives.colors.warm_surface.main,
      dark: Primitives.colors.warm_surface.dark,
    },
  },
  shape: {
    borderRadius: 3,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Skip elements with data-native-overflow so they keep the platform overlay scrollbar.
        '*:not([data-native-overflow])::-webkit-scrollbar': {
          width: 9,
          height: 9,
        },
        '*:not([data-native-overflow])::-webkit-scrollbar-track': {
          background: 'transparent',
          border: 'none',
        },
        '*:not([data-native-overflow])::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
          borderRadius: 99,
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
          transition: 'background-color 0.2s',
        },
        '*:not([data-native-overflow]):hover::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
        },
        'body[data-dragging] .MuiTooltip-popper': {
          display: 'none',
        },
        'body[data-menu-open] .MuiTooltip-popper:not(.menu-action-tooltip)': {
          display: 'none',
        },
      },
    },
    MuiChip: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          '&&': {
            height: 16,
            borderRadius: 3,
            border: '1px solid',
            '--label-color': `var(--label-border-color, ${
              ownerState.color && ownerState.color !== 'default'
                ? theme.palette[ownerState.color].main
                : theme.palette.divider
            })`,
            borderColor:
              'oklch(from var(--label-color) 0.89 min(0.08, calc(c * 2)) h / 1)',
            backgroundColor:
              'oklch(from var(--label-color) 0.95 min(0.04, c) h / 1)',
            color:
              'oklch(from var(--label-color) 0.55 min(0.2, calc(c * 4)) h / 1)',
            fontSize: 8,
            fontWeight: 600,
            letterSpacing: '0.35px',
            textTransform: 'uppercase',
            lineHeight: 1,
            '&:hover': {
              backgroundColor:
                'oklch(from var(--label-color) 0.95 min(0.04, c) h / 1)',
            },
          },
        }),
        label: {
          padding: '0 3px',
          lineHeight: 'normal',
        },
        deleteIcon: {
          width: 10,
          height: 10,
          fontSize: 10,
          margin: '0 3px 0 0',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        // Name of the slot
        root: {
          minHeight: 36,
          padding: '4px 8px',
          fontSize: 12,
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'rgba(0,0,0,0.05)',
          },
        },
      },
    },
    // v9 uses theme.spacing(4.5) (18px with Nodon spacing 4). v7 was 56px.
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 56,
        },
      },
    },
    MuiSelect: {
      defaultProps: { IconComponent: DropdownIndicator },
    },
    MuiNativeSelect: {
      defaultProps: { IconComponent: DropdownIndicator },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&.Mui-disabled::placeholder': {
            opacity: 1, // default is
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.custom.focus,
            borderWidth: 1,
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 20px 0px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          border: '1px solid rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    MuiIconButton: {
      defaultProps: { disableRipple: true },
    },
    MuiButton: {
      defaultProps: { disableRipple: true, disableElevation: true },
      styleOverrides: {
        root: {
          minWidth: 'auto',
          '&&': {
            height: 22,
            padding: '0 12px',
            backgroundColor: '#fff',
            border: 'none',
            borderRadius: 4,
            boxShadow: 'none',
            color: 'rgba(0,0,0,0.8)',
            fontSize: 11,
            fontWeight: 500,
            lineHeight: '13px',
            outline: '1px solid rgba(28,28,28,0.2)',
            textTransform: 'none',
            '& .MuiButton-startIcon > :nth-of-type(1), & .MuiButton-endIcon > :nth-of-type(1)':
              {
                fontSize: 12,
                width: 12,
                height: 12,
              },
            '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
              width: 12,
              height: 12,
            },
            '&:hover': {
              backgroundColor: '#fff',
              boxShadow: 'none',
              outline: '1px solid rgba(28,28,28,0.4)',
            },
            '&:active': { backgroundColor: '#fff', boxShadow: 'none' },
            '&.Mui-focusVisible': {
              boxShadow: 'none',
              outline: '1px solid rgba(28,28,28,0.4)',
              outlineOffset: 0,
            },
            '&.Mui-disabled': {
              color: 'rgba(0,0,0,0.8)',
              backgroundColor: '#fff',
              opacity: 0.4,
            },
            '&.MuiButton-sizeLarge': ghostButtonLargeStyles,
            '&.GhostButton-root.MuiButton-sizeLarge': { padding: '0 12px' },
            '&.MuiButton-contained.MuiButton-colorPrimary':
              ghostButtonPrimaryStyles,
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
      },
      styleOverrides: {
        tooltip: ({ theme }) => getTooltipBaseStyles(theme),
        arrow: ({ theme }) => ({
          color: theme.palette.grey[900],
        }),
      },
    },
  },
  spacing: 4,
});

export const NodonTheme = responsiveFontSizes(baseTheme);

declare module '@mui/material/styles' {
  interface ZIndex {
    readonlyBlocker: number;
  }

  interface Palette {
    custom: {
      focus: string;
    };
    neutral: PaletteColor & {
      lighter: string;
      lightest: string;
      superLight: string;
      medium: string;
    };
    styledBackground: PaletteColor;
    warmSurface: PaletteColor;
  }

  interface TypeText {
    subtle: string;
  }

  // allow configuration using `createTheme`
  interface PaletteOptions {
    custom?: {
      focus: string;
    };
    neutral?: PaletteColorOptions & {
      lighter: string;
      lightest: string;
      superLight: string;
      medium: string;
    };
    styledBackground?: PaletteColorOptions;
    warmSurface?: PaletteColorOptions;
  }
}

// Update the Button's color prop options
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
    styledBackground: true;
  }
}
